export const surveyGamified = {
  "title": "Find Your Game Night Superpower",
  "description": "Take the 2-minute quiz to discover your board game personality — and get personalized Pista recommendations.",
  "logoPosition": "right",
  "completedHtml": "<div style='text-align: center; padding: 20px;'><h2>🎉 Your game night superpower is unlocked!</h2><p>Check your email for your full personality guide and early access to Pista.</p><br><a href='https://app.voyagerbug.com' style='background: #4F46E5; color: white; padding: 12px 24px; border-radius: 8px; text-decoration: none;'>Explore Pista</a></div>",
  "pages": [
    {
      "name": "page_hook",
      "title": "Let's find your game night superpower",
      "description": "Answer a few quick questions — no right or wrong answers.",
      "elements": [
        {
          "type": "radiogroup",
          "name": "game_night_thought",
          "title": "Your phone buzzes. \"Game night tonight!\" Your first thought is...",
          "isRequired": true,
          "choices": [
            { "value": "frequent", "text": "🎲 \"Yes! Which game should we play?\"" },
            { "value": "regular", "text": "🕐 \"I need to check my schedule — but I want to\"" },
            { "value": "occasional", "text": "📅 \"Love to, but let me see when I'm free\"" },
            { "value": "rare", "text": "💭 \"I miss game nights — it's been a while\"" }
          ]
        }
      ]
    },
    {
      "name": "page_redirect",
      "visibleIf": "{game_night_thought} = 'rare'",
      "elements": [
        {
          "type": "html",
          "name": "redirect_message",
          "html": "<div style='text-align: center; padding: 40px 20px;'><h3>We'd love to have you back at the table!</h3><p>Here are some resources to restart your game nights.</p><br><a href='https://app.voyagerbug.com' class='btn' style='display: inline-block; background: #4F46E5; color: white; padding: 12px 24px; border-radius: 8px; text-decoration: none;'>Explore Pista</a></div>"
        }
      ]
    },
    {
      "name": "page_pain_points",
      "visibleIf": "{game_night_thought} != 'rare'",
      "elements": [
        {
          "type": "radiogroup",
          "name": "pain_point",
          "title": "You have 2 hours for game night. Which scenario stresses you out most?",
          "isRequired": true,
          "choices": [
            { "value": "decision_paralysis", "text": "🤔 \"We spend 20 minutes staring at the shelf, vetoing each other's suggestions\"" },
            { "value": "rules_friction", "text": "📚 \"I have to relearn the rules for the game someone picked\"" },
            { "value": "discovery_risk", "text": "💸 \"Someone suggests a game I've never heard of — is it worth our time?\"" },
            { "value": "discovery_friction", "text": "🔍 \"I know there's a game I want to play, but I can't remember what it's called\"" },
            { "value": "price_availability", "text": "🛒 \"I realize the game we want is out of print or $100+\"" },
            { "value": "stagnation", "text": "😴 \"None of these — my group just plays the same 3 games every time\"" }
          ]
        }
      ]
    },
    {
      "name": "page_research_style",
      "visibleIf": "{game_night_thought} != 'rare'",
      "elements": [
        {
          "type": "checkbox",
          "name": "research_methods",
          "title": "A new game catches your eye. Your next move is...",
          "description": "Select all that apply",
          "isRequired": true,
          "choices": [
            { "value": "bgg_ratings", "text": "📊 \"Check its BGG rating and rank\"" },
            { "value": "video_reviews", "text": "🎥 \"Watch a review (Dice Tower, SU&SD)\"" },
            { "value": "playthroughs", "text": "🎬 \"Find a playthrough video\"" },
            { "value": "ask_friends", "text": "👥 \"Ask my game group if anyone has played it\"" },
            { "value": "digital_try", "text": "🎮 \"See if it's on Board Game Arena to try first\"" },
            { "value": "cafe_try", "text": "☕ \"Go to a board game cafe to test it\"" },
            { "value": "written_reviews", "text": "📝 \"Read written reviews on BGG or Reddit\"" }
          ]
        }
      ]
    },
    {
      "name": "page_game_types",
      "visibleIf": "{game_night_thought} != 'rare'",
      "elements": [
        {
          "type": "checkbox",
          "name": "game_types",
          "title": "Pick your starting hand. Choose up to 3.",
          "description": "Select up to 3",
          "isRequired": true,
          "choices": [
            { "value": "strategy", "text": "🏭 \"I love optimizing engines and collecting resources\"" },
            { "value": "thematic", "text": "🎭 \"Theme and story matter more than mechanics\"" },
            { "value": "cooperative", "text": "🤝 \"Let's work together against the game\"" },
            { "value": "competitive", "text": "⚔️ \"I want to crush my friends (in a fun way)\"" },
            { "value": "party", "text": "🃏 \"Short rules, lots of laughs\"" },
            { "value": "abstract", "text": "🧩 \"Give me elegant systems and perfect information\"" },
            { "value": "deck_builder", "text": "🧛 \"I like building decks and combos\"" },
            { "value": "war_games", "text": "🗺️ \"I want to paint minis and conquer territories\"" },
            { "value": "rpg", "text": "🎲 \"I'd rather tell a story than win\"" }
          ],
          "maxSelectedChoices": 3
        }
      ]
    },
    {
      "name": "page_group_dynamics",
      "visibleIf": "{game_night_thought} != 'rare'",
      "elements": [
        {
          "type": "checkbox",
          "name": "play_group",
          "title": "Your ideal game night table has...",
          "description": "Select all that apply",
          "isRequired": true,
          "choices": [
            { "value": "solo", "text": "🧘 \"Just me — solo gaming is my zen\"" },
            { "value": "partner", "text": "💑 \"My partner — we play together most often\"" },
            { "value": "family", "text": "👨‍👩‍👧‍👦 \"My family (kids or extended)\"" },
            { "value": "friends_casual", "text": "🍻 \"My friends — we're here to have fun\"" },
            { "value": "hobbyist_group", "text": "🧠 \"A dedicated group — we meet weekly for heavy games\"" },
            { "value": "online", "text": "💻 \"Whoever shows up online\"" }
          ]
        }
      ]
    },
    {
      "name": "page_tech_comfort",
      "visibleIf": "{game_night_thought} != 'rare'",
      "elements": [
        {
          "type": "radiogroup",
          "name": "digital_openness",
          "title": "A digital tool promises to solve your biggest game night frustration. You think...",
          "isRequired": true,
          "choices": [
            { "value": "very_open", "text": "🚀 \"Finally! Where do I sign up?\"" },
            { "value": "somewhat_open", "text": "🤔 \"Interesting — I'd try it if it's free\"" },
            { "value": "neutral", "text": "😐 \"I'd look at it, but I'm skeptical\"" },
            { "value": "not_open", "text": "📵 \"No phones at the game table, thanks\"" },
            { "value": "already_using", "text": "🔧 \"I already use something for this\"" }
          ]
        },
        {
          "type": "checkbox",
          "name": "digital_blockers",
          "visibleIf": "{digital_openness} = 'not_open' or {digital_openness} = 'neutral'",
          "title": "We get it. What's your biggest hesitation?",
          "choices": [
            "I don't want screens at the game table",
            "I don't trust apps with my data",
            "I don't want to pay for hobby tools",
            "I prefer analog methods (paper, memory, BGG)",
            "Other"
          ],
          "showOtherItem": true,
          "otherText": "Other:"
        }
      ]
    },
    {
      "name": "page_features",
      "visibleIf": "{game_night_thought} != 'rare'",
      "elements": [
        {
          "type": "matrix",
          "name": "feature_interest",
          "title": "How useful would each of these be for YOUR game nights?",
          "columns": [
            { "value": 1, "text": "Not useful" },
            { "value": 2, "text": "Slightly useful" },
            { "value": 3, "text": "Moderately useful" },
            { "value": 4, "text": "Very useful" },
            { "value": 5, "text": "Essential" }
          ],
          "rows": [
            { "value": "mood_matcher", "text": "A tool that recommends games based on your mood, player count, and available time" },
            { "value": "online_checker", "text": "A tool that shows which games in your collection can be played online right now" },
            { "value": "price_finder", "text": "A tool that finds the best price (new, used, or online) for a game" },
            { "value": "similarity_finder", "text": "A tool that shows games similar to ones you love (and filters ones you disliked)" },
            { "value": "rules_refresher", "text": "A quick rules refresher for games you haven't played recently" },
            { "value": "play_tracker", "text": "A tool that tracks your plays and shows stats over time" },
            { "value": "shared_collection", "text": "A shared collection view for households or game groups" }
          ]
        }
      ]
    },
    {
      "name": "page_pricing",
      "visibleIf": "{game_night_thought} != 'rare'",
      "elements": [
        {
          "type": "radiogroup",
          "name": "willingness_to_pay",
          "title": "If Pista saved you 1-2 hours per week on game night prep, what's it worth to you?",
          "isRequired": true,
          "choices": [
            { "value": "free_only", "text": "🎁 \"A free forever tier\"" },
            { "value": "low", "text": "☕ \"About 2 fancy coffees per year ($25-30)\"" },
            { "value": "medium", "text": "🍕 \"A pizza night per year ($45-50)\"" },
            { "value": "one_time", "text": "🎲 \"One new game per year ($15-25 one-time)\"" },
            { "value": "no_pay", "text": "💰 \"I don't pay for hobby tools\"" }
          ]
        }
      ]
    },
    {
      "name": "page_result",
      "visibleIf": "{game_night_thought} != 'rare'",
      "elements": [
        {
          "type": "html",
          "name": "persona_result",
          "html": "<div style='text-align: center; padding: 20px;'><div style='font-size: 48px; margin-bottom: 16px;'>{persona_icon}</div><h2>You are <span style='color: #4F46E5;'>{persona_title}</span></h2><p style='font-size: 18px; margin: 16px 0;'>{persona_description}</p><div style='background: #F3F4F6; border-radius: 12px; padding: 16px; margin: 20px 0;'><p><strong>🎯 Your game night superpower:</strong> {persona_superpower}</p><p><strong>🔍 You match:</strong> {persona_match}% with other {persona_title_short}</p><p><strong>✨ Your Pista starter pack:</strong> {persona_features}</p></div><div style='margin-top: 24px;'><button id='shareBtn' style='background: #1DA1F2; color: white; border: none; padding: 10px 20px; border-radius: 20px; margin: 0 8px; cursor: pointer;'>🐦 Share on X</button><button id='copyBtn' style='background: #4F46E5; color: white; border: none; padding: 10px 20px; border-radius: 20px; margin: 0 8px; cursor: pointer;'>📋 Copy Link</button></div><div style='margin-top: 32px;'><input type='email' id='email_capture' placeholder='your@email.com' style='padding: 12px; width: 280px; border: 1px solid #D1D5DB; border-radius: 8px;'><button id='submitEmail' style='background: #10B981; color: white; border: none; padding: 12px 24px; border-radius: 8px; margin-left: 8px; cursor: pointer;'>Get My Starter Pack →</button></div><p style='font-size: 12px; color: #6B7280; margin-top: 20px;'>We'll email your results and early access. No spam.</p></div>"
        }
      ]
    }
  ],
  "calculatedValues": [
    {
      "name": "persona_title",
      "expression": "iif({game_types} contains 'strategy' and {research_methods} contains 'bgg_ratings' and {play_group} contains 'solo', 'The Solo Strategist', iif({research_methods} contains 'digital_try' or {research_methods} contains 'cafe_try', 'The Try-Before-You-Buyer', iif({play_group} contains 'friends_casual' or {play_group} contains 'family', 'The Social Gamer', iif({pain_point} = 'decision_paralysis' or {pain_point} = 'rules_friction', 'The Host', 'The Curator'))))"
    },
    {
      "name": "persona_icon",
      "expression": "iif({persona_title} = 'The Solo Strategist', '🧘', iif({persona_title} = 'The Try-Before-You-Buyer', '🎮', iif({persona_title} = 'The Social Gamer', '🍻', iif({persona_title} = 'The Host', '🏠', '📚'))))"
    },
    {
      "name": "persona_description",
      "expression": "iif({persona_title} = 'The Solo Strategist', 'You play to master systems, not to beat others. The only opponent you need is your last high score.', iif({persona_title} = 'The Try-Before-You-Buyer', 'You never waste money on a game that doesn't fit. Your shelf is full of proven winners.', iif({persona_title} = 'The Social Gamer', 'For you, games are about connection, not competition. Your laugh track is the best sound at the table.', iif({persona_title} = 'The Host', 'Your table is always full because you make game night effortless. You're the glue that holds the group together.', 'You don't just play games — you collect, study, and curate. Your game nights are legendary because you've already done the homework.'))))"
    },
    {
      "name": "persona_superpower",
      "expression": "iif({persona_title} = 'The Solo Strategist', 'Turning 2 hours of solitude into a deeply satisfying victory', iif({persona_title} = 'The Try-Before-You-Buyer', 'Spotting a hidden gem before anyone else', iif({persona_title} = 'The Social Gamer', 'Making everyone feel welcome at the table', iif({persona_title} = 'The Host', 'Getting any group to the fun part in under 5 minutes', 'Finding the perfect game for any mood or player count'))))"
    },
    {
      "name": "persona_title_short",
      "expression": "iif({persona_title} = 'The Solo Strategist', 'Solo Strategists', iif({persona_title} = 'The Try-Before-You-Buyer', 'Try-Before-You-Buyers', iif({persona_title} = 'The Social Gamer', 'Social Gamers', iif({persona_title} = 'The Host', 'Hosts', 'Curators'))))"
    },
    {
      "name": "persona_match",
      "expression": "iif({persona_title} = 'The Solo Strategist', '94', iif({persona_title} = 'The Try-Before-You-Buyer', '87', iif({persona_title} = 'The Social Gamer', '91', iif({persona_title} = 'The Host', '89', '93'))))"
    },
    {
      "name": "persona_features",
      "expression": "iif({persona_title} = 'The Solo Strategist', 'Play tracking + Stats dashboard + Solo mode finder', iif({persona_title} = 'The Try-Before-You-Buyer', 'Online play badge + BGA integration + Price comparison', iif({persona_title} = 'The Social Gamer', 'Party game recommendations + Shared collections + Player count optimizer', iif({persona_title} = 'The Host', 'Mood matcher + Quick rules refresher + Decision engine', 'Similarity search + Collection insights + Price tracker'))))"
    }
  ],
  "showProgressBar": true,
  "progressBarLocation": "top",
  "checkErrorsMode": "onValueChanged",
  "startSurveyText": "Start Quiz",
  "completeText": "Reveal My Superpower",
  "widthMode": "responsive"
};


export const surveyGamifiedV2 = {
  "title": "Find Your Game Night Superpower",
  "description": "Take the 2-minute quiz to discover your board game personality — and get personalized Pista recommendations.",
  "logoPosition": "right",
  "completedHtml": "<div style='text-align: center; padding: 20px;'><h2>🎉 Your game night superpower is unlocked!</h2><p>Check your email for your full personality guide and early access to Pista.</p><br><a href='https://app.voyagerbug.com' style='background: #4F46E5; color: white; padding: 12px 24px; border-radius: 8px; text-decoration: none;'>Explore Pista</a></div>",
  "pages": [
    {
      "name": "page_hook",
      "title": "Let's find your game night superpower",
      "description": "Answer a few quick questions — no right or wrong answers.",
      "elements": [
        {
          "type": "radiogroup",
          "name": "game_night_thought",
          "title": "Your phone buzzes: \"Game night tonight!\" You hit auto-reply. Which response is most like you?",
          "isRequired": true,
          "choices": [
            {
              "value": "on_my_way",
              "text": "🏃 On my way."
            },
            {
              "value": "what_are_we_playing",
              "text": "🎲 What are we playing?"
            },
            {
              "value": "send_address",
              "text": "📍 Send me the address again?"
            },
            {
              "value": "rain_check",
              "text": "🌧️ Rain check?"
            },
            {
              "value": "new_phone",
              "text": "📱 New phone, who dis?"
            }
          ]
        }
      ]
    },
    {
      "name": "page_pain_points",
      "elements": [
        {
          "type": "radiogroup",
          "name": "pain_point",
          "title": "You have 2 hours for game night. Which scenario stresses you out most?",
          "isRequired": true,
          "choices": [
            {
              "value": "decision_paralysis",
              "text": "🤔 \"We spend 20 minutes staring at the shelf, vetoing each other's suggestions\""
            },
            {
              "value": "rules_friction",
              "text": "📚 \"I have to relearn the rules for the game someone picked\""
            },
            {
              "value": "discovery_risk",
              "text": "💸 \"Someone suggests a game I've never heard of — is it worth our time?\""
            },
            {
              "value": "discovery_friction",
              "text": "🔍 \"I'm in the mood for a certain type of game but I'm not sure which game I want to play\""
            },
            {
              "value": "price_availability",
              "text": "🛒 \"I realize the game we want is out of print or €100+\""
            },
            {
              "value": "stagnation",
              "text": "😴 \"None of these — my group just plays the same 3 games every time\""
            }
          ]
        }
      ]
    },
    {
      "name": "page_research_style",
      "elements": [
        {
          "type": "checkbox",
          "name": "research_methods",
          "title": "A new game catches your eye. Your next move is...",
          "description": "Select all that apply",
          "isRequired": true,
          "choices": [
            {
              "value": "bgg_ratings",
              "text": "📊 Check its BGG rating and rank"
            },
            {
              "value": "video_reviews",
              "text": "🎥 Watch a review (Dice Tower, Rahdo, SU&SD)"
            },
            {
              "value": "playthroughs",
              "text": "🎬 Find a playthrough video"
            },
            {
              "value": "ask_friends",
              "text": "👥 Ask my game group if anyone has played it"
            },
            {
              "value": "digital_try",
              "text": "🎮 See if it's on Board Game Arena to try first"
            },
            {
              "value": "cafe_try",
              "text": "☕ Go to a board game cafe to test it"
            },
            {
              "value": "written_reviews",
              "text": "📝 Read written reviews on BGG or Reddit"
            }
          ]
        }
      ]
    },
    {
      "name": "page_game_types",
      "elements": [
        {
          "type": "checkbox",
          "name": "game_types",
          "title": "Pick your ideal hand. Choose up to 3.",
          "description": "Select up to 3",
          "isRequired": true,
          "choices": [
            {
              "value": "strategy",
              "text": "🏭 \"I love optimizing engines and collecting resources\""
            },
            {
              "value": "thematic",
              "text": "🎭 \"Theme and story matter more than mechanics\""
            },
            {
              "value": "cooperative",
              "text": "🤝 \"Let's work together against the game\""
            },
            {
              "value": "competitive",
              "text": "⚔️ \"I want to crush my friends (in a fun way)\""
            },
            {
              "value": "party",
              "text": "🃏 \"Short rules, lots of laughs\""
            },
            {
              "value": "abstract",
              "text": "🧩 \"Give me elegant systems and perfect information\""
            },
            {
              "value": "deck_builder",
              "text": "🧛 \"I like building decks and combos\""
            },
            {
              "value": "war_games",
              "text": "🎨 \"I want to paint minis and conquer territories\""
            },
            {
              "value": "rpg",
              "text": "🗺️ \"I'd rather tell a story than win\""
            }
          ],
          "maxSelectedChoices": 3
        }
      ]
    },
    {
      "name": "page_group_dynamics",
      "elements": [
        {
          "type": "checkbox",
          "name": "play_group",
          "title": "Your ideal game night table has...",
          "description": "Select all that apply",
          "isRequired": true,
          "choices": [
            {
              "value": "solo",
              "text": "🧘 \"Just me — solo gaming is my zen\""
            },
            {
              "value": "partner",
              "text": "💑 \"My partner — we play together most often\""
            },
            {
              "value": "family",
              "text": "👨‍👩‍👧‍👦 \"My family (kids or extended)\""
            },
            {
              "value": "friends_casual",
              "text": "🍻 \"My friends — we're here to have fun\""
            },
            {
              "value": "hobbyist_group",
              "text": "🧠 \"A dedicated group — we meet weekly for heavy games\""
            },
            {
              "value": "online",
              "text": "💻 \"Whoever shows up online\""
            }
          ]
        }
      ]
    },
    {
      "name": "page_online_realm",
      "elements": [
        {
          "type": "radiogroup",
          "name": "online_realm",
          "title": "⚔️ You cast your eyes over your realm. What do you see?",
          "isRequired": true,
          "choices": [
            {
              "value": "physical",
              "text": "🏠 Wood and cardboard — my table is sacred, digital is for video games"
            },
            {
              "value": "hybrid",
              "text": "🌐 A hybrid kingdom — I conquer across both physical and digital tables"
            },
            {
              "value": "digital",
              "text": "💻 Glowing screens — my table is wherever I log in"
            }
          ]
        }
      ]
    },
    {
      "name": "page_tech_comfort",
      "elements": [
        {
          "type": "radiogroup",
          "name": "digital_openness",
          "title": "A digital tool promises to solve your biggest game night frustration. You think...",
          "isRequired": true,
          "choices": [
            {
              "value": "very_open",
              "text": "🚀 \"Finally! Where do I sign up?\""
            },
            {
              "value": "somewhat_open",
              "text": "🤔 \"Interesting — I'd try it if it's free\""
            },
            {
              "value": "neutral",
              "text": "😐 \"I'd look at it, but I'm skeptical\""
            },
            {
              "value": "not_open",
              "text": "📵 \"No phones at the game table, thanks\""
            },
            {
              "value": "already_using",
              "text": "🔧 \"I already use something for this\""
            }
          ]
        },
        {
          "type": "checkbox",
          "name": "digital_blockers",
          "visibleIf": "{digital_openness} = 'not_open' or {digital_openness} = 'neutral'",
          "title": "We get it. What's your biggest hesitation?",
          "choices": [
            "I don't want screens at the game table",
            "I don't trust apps with my data",
            "I don't want to pay for hobby tools",
            "I prefer analog methods (paper, memory, BGG)"
          ],
          "showOtherItem": true,
          "otherPlaceholder": "(did we miss something?)",
          "otherText": "Other:"
        }
      ]
    },
    {
      "name": "page_features",
      "elements": [
        {
          "type": "matrix",
          "name": "feature_interest2",
          "title": "⚔️ You're assembling your ultimate gear before the Boss Battle of Game Night. Rate each artifact:",
          "columns": [
            {
              "value": "Definite_yes",
              "text": "🏆 Critical hit! Yes!!"
            },
            {
              "value": "Cautious_yes",
              "text": "🎲 Worth a roll — I'd try it"
            },
            {
              "value": "No",
              "text": "❌ Not for me"
            }
          ],
          "rows": [
            {
              "value": "orb_of_perfect_picks:_it_reads_the_room's_vibes_and_reveals_the_exact_game_your_party_craves",
              "text": "Orb of Perfect Picks: It reads the room's vibes and reveals the exact game your party craves",
              "icon": "🔮"
            },
            {
              "value": "blade_of_rediscovery:_it_whispers_of_forgotten_treasures_buried_deep_in_your_collection,_waiting_to_be_unsheathed_again",
              "text": "Blade of Rediscovery: It whispers of forgotten treasures buried deep in your collection, waiting to be unsheathed again",
              "icon": "🗡️"
            },
            {
              "value": "coin_of_clever_bargains:_it_sniffs_out_the_best_price_for_any_game,_new,_used,_or_online_in_the_realm",
              "text": "Coin of Clever Bargains: It sniffs out the best price for any game, new, used, or online in the realm",
              "icon": "💰"
            },
            {
              "value": "tome_of_taste:_it_shows_you_games_kin_to_your_favourites_and_banishes_the_ones_you'd_never_touch",
              "text": "Tome of Taste: It shows you games kin to your favourites and banishes the ones you'd never touch",
              "icon": "🧙‍"
            },
            {
              "value": "scroll_of_swift_recall:_helps_you_remember_the_rules_for_games_you_haven't_touched_in_moons",
              "text": "Scroll of Swift Recall: Helps you remember the rules for games you haven't touched in moons",
              "icon": "📜"
            },
            {
              "value": "map_of_glory:_it_charts_every_victory,_every_defeat,_and_every_legendary_streak_across_your_game_nights",
              "text": "Map of Glory: It charts every victory, every defeat, and every legendary streak across your game nights",
              "icon": "🗺️"
            },
            {
              "value": "🏰 Vault of Unity: It binds your household or fellowship into one shared view of games you collectively own",
              "text": "🏰 The Fellowship of the Collection: a shared view of games in your household or group"
            }
          ],
          "customType": "icon-button-matrix"
        }
      ]
    },
    {
      "name": "page_pricing",
      "elements": [
        {
          "type": "radiogroup",
          "name": "willingness_to_pay",
          "title": "🌹 If an app helped you fall back in love with gaming — no stress, no friction, just the good stuff — what would you pay for that?",
          "isRequired": true,
          "choices": [
            {
              "value": "Free_ads",
              "text": "🎁 A free forever tier (with those bloody ads)"
            },
            {
              "value": "1euro",
              "text": "🎈 One packet of 5 party balloons (assorted colours of course) (€1/month)"
            },
            {
              "value": "3.5euros",
              "text": "🧦 One single novelty sock (not a pair) (€3.5/month)"
            },
            {
              "value": "5euros",
              "text": "🥨 One giant novelty pretzel from a carnival kiosk (plain, no chocolate) (€5/month)"
            }
          ]
        }
      ]
    },
    {
      "name": "page_result",
      "elements": [
        {
          "type": "html",
          "name": "persona_result",
          "html": "<div style='text-align: center; padding: 20px; max-width: 700px; margin: 0 auto;'><div style='font-size: 64px; margin-bottom: 8px;'>{persona_icon}</div><h2 style='font-size: 24px; color: #6B7280; margin-bottom: 4px;'>You are</h2><h1 style='font-size: 36px; color: #4F46E5; margin-top: 0; margin-bottom: 20px;'>{persona_title}</h1><p style='font-size: 18px; line-height: 1.6; color: #374151; margin: 16px 0 24px 0;'>{persona_description}</p><div style='background: #F3F4F6; border-radius: 12px; padding: 20px; margin: 20px 0; text-align: left;'><p style='margin: 8px 0; font-size: 16px;'><strong>🎯 Your game night superpower:</strong> {persona_superpower}</p><p style='margin: 8px 0; font-size: 16px;'><strong>🔍 You match:</strong> {persona_match}% with other {persona_title_short}</p></div><div id='survey-share-actions' style='margin-top: 32px;'></div><div id='survey-email-capture' style='margin-top: 32px;'></div></div>"
        }
      ]
    }
  ],
  "calculatedValues": [
    {
      "name": "base_persona",
      "expression": "iif({game_types} contains 'strategy' and {research_methods} contains 'bgg_ratings' and {play_group} contains 'solo', 'The Solo Strategist', iif({research_methods} contains 'digital_try' or {research_methods} contains 'cafe_try', 'The Try-Before-You-Buyer', iif({play_group} contains 'friends_casual' or {play_group} contains 'family', 'The Social Gamer', iif({pain_point} = 'decision_paralysis' or {pain_point} = 'rules_friction', 'The Host', 'The Curator'))))"
    },
    {
      "name": "persona_title",
      "expression": "iif({base_persona} = 'The Solo Strategist' and {online_realm} = 'digital', 'The Solo Strategist — Netrunner Edition', iif({base_persona} = 'The Solo Strategist' and {online_realm} = 'hybrid', 'The Solo Strategist — Digital Diplomat Edition', iif({base_persona} = 'The Solo Strategist', 'The Solo Strategist', iif({base_persona} = 'The Try-Before-You-Buyer' and {online_realm} = 'digital', 'The Try-Before-You-Buyer — Digital Tester', iif({base_persona} = 'The Try-Before-You-Buyer', 'The Try-Before-You-Buyer', iif({base_persona} = 'The Social Gamer' and {online_realm} = 'digital', 'The Social Gamer — Online Ambassador', iif({base_persona} = 'The Social Gamer' and {online_realm} = 'hybrid', 'The Social Gamer — Hybrid Connector', iif({base_persona} = 'The Social Gamer', 'The Social Gamer', iif({base_persona} = 'The Host' and {online_realm} = 'digital', 'The Host — Virtual Host', iif({base_persona} = 'The Host' and {online_realm} = 'hybrid', 'The Host — Hybrid Host', iif({base_persona} = 'The Host', 'The Host', iif({base_persona} = 'The Curator' and {online_realm} = 'digital', 'The Curator — Digital Curator', iif({base_persona} = 'The Curator', 'The Curator', 'The Curator')))))))))))))"
    },
    {
      "name": "persona_icon",
      "expression": "iif({base_persona} = 'The Solo Strategist' and {online_realm} = 'digital', '🧘‍♂️💻', iif({base_persona} = 'The Solo Strategist' and {online_realm} = 'hybrid', '🧘‍♂️🌐', iif({base_persona} = 'The Solo Strategist', '🧘', iif({base_persona} = 'The Try-Before-You-Buyer' and {online_realm} = 'digital', '🎮💻', iif({base_persona} = 'The Try-Before-You-Buyer', '🎮', iif({base_persona} = 'The Social Gamer' and {online_realm} = 'digital', '🍻💻', iif({base_persona} = 'The Social Gamer' and {online_realm} = 'hybrid', '🍻🌐', iif({base_persona} = 'The Social Gamer', '🍻', iif({base_persona} = 'The Host' and {online_realm} = 'digital', '🏠💻', iif({base_persona} = 'The Host' and {online_realm} = 'hybrid', '🏠🌐', iif({base_persona} = 'The Host', '🏠', iif({base_persona} = 'The Curator' and {online_realm} = 'digital', '📚💻', iif({base_persona} = 'The Curator', '📚', '📚'))))))))))))"
    },
    {
      "name": "persona_description",
      "expression": "iif({base_persona} = 'The Solo Strategist' and {online_realm} = 'digital', 'You play to master systems across both physical and digital realms. Your solo gaming spans cardboard and screens, and your BGA stats are legendary. The only opponent you need is your last high score.', iif({base_persona} = 'The Solo Strategist' and {online_realm} = 'hybrid', 'You flow seamlessly between solo physical gaming and digital conquests. Your realm is everywhere, and your mastery is unmatched.', iif({base_persona} = 'The Solo Strategist', 'You play to master systems, not to beat others. The only opponent you need is your last high score.', iif({base_persona} = 'The Try-Before-You-Buyer' and {online_realm} = 'digital', 'You never waste money on a game that does not fit. Your shelf is full of proven winners — and you have tested every one online first.', iif({base_persona} = 'The Try-Before-You-Buyer', 'You never waste money on a game that does not fit. Your shelf is full of proven winners.', iif({base_persona} = 'The Social Gamer' and {online_realm} = 'digital', 'For you, games are about connection, not competition. Your laugh track echoes across both physical tables and digital lobbies.', iif({base_persona} = 'The Social Gamer' and {online_realm} = 'hybrid', 'For you, games are about connection, not competition. You bring people together across both physical and digital tables.', iif({base_persona} = 'The Social Gamer', 'For you, games are about connection, not competition. Your laugh track is the best sound at the table.', iif({base_persona} = 'The Host' and {online_realm} = 'digital', 'Your table is always full — even when it is virtual. You make game night effortless no matter where your players are.', iif({base_persona} = 'The Host' and {online_realm} = 'hybrid', 'Your table spans both worlds. Whether in-person or online, you are the glue that holds the group together.', iif({base_persona} = 'The Host', 'Your table is always full because you make game night effortless. You are the glue that holds the group together.', iif({base_persona} = 'The Curator' and {online_realm} = 'digital', 'You do not just play games — you collect, study, and curate across physical and digital libraries. Your game nights are legendary because you have already done the homework.', iif({base_persona} = 'The Curator', 'You do not just play games — you collect, study, and curate. Your game nights are legendary because you have already done the homework.', 'You do not just play games — you collect, study, and curate. Your game nights are legendary because you have already done the homework.')))))))))))))"
    },
    {
      "name": "persona_superpower",
      "expression": "iif({base_persona} = 'The Solo Strategist' and {online_realm} = 'digital', 'Turning 2 hours of solitude into a deeply satisfying victory — on cardboard or screen', iif({base_persona} = 'The Solo Strategist' and {online_realm} = 'hybrid', 'Mastering any system, anywhere, anytime', iif({base_persona} = 'The Solo Strategist', 'Turning 2 hours of solitude into a deeply satisfying victory', iif({base_persona} = 'The Try-Before-You-Buyer' and {online_realm} = 'digital', 'Spotting a hidden gem before anyone else — then proving it online', iif({base_persona} = 'The Try-Before-You-Buyer', 'Spotting a hidden gem before anyone else', iif({base_persona} = 'The Social Gamer' and {online_realm} = 'digital', 'Making everyone feel welcome — no matter which table they are at', iif({base_persona} = 'The Social Gamer' and {online_realm} = 'hybrid', 'Building bridges between physical and digital players', iif({base_persona} = 'The Social Gamer', 'Making everyone feel welcome at the table', iif({base_persona} = 'The Host' and {online_realm} = 'digital', 'Getting any group to the fun part in under 5 minutes — even on mute', iif({base_persona} = 'The Host' and {online_realm} = 'hybrid', 'Seamlessly blending in-person and online game nights', iif({base_persona} = 'The Host', 'Getting any group to the fun part in under 5 minutes', iif({base_persona} = 'The Curator' and {online_realm} = 'digital', 'Finding the perfect game for any mood, player count, or platform', iif({base_persona} = 'The Curator', 'Finding the perfect game for any mood or player count', 'Finding the perfect game for any mood or player count'))))))))))))"
    },
    {
      "name": "persona_title_short",
      "expression": "iif({base_persona} = 'The Solo Strategist' and {online_realm} = 'digital', 'Digital Solo Strategists', iif({base_persona} = 'The Solo Strategist' and {online_realm} = 'hybrid', 'Hybrid Solo Strategists', iif({base_persona} = 'The Solo Strategist', 'Solo Strategists', iif({base_persona} = 'The Try-Before-You-Buyer' and {online_realm} = 'digital', 'Digital Testers', iif({base_persona} = 'The Try-Before-You-Buyer', 'Try-Before-You-Buyers', iif({base_persona} = 'The Social Gamer' and {online_realm} = 'digital', 'Digital Social Gamers', iif({base_persona} = 'The Social Gamer' and {online_realm} = 'hybrid', 'Hybrid Social Gamers', iif({base_persona} = 'The Social Gamer', 'Social Gamers', iif({base_persona} = 'The Host' and {online_realm} = 'digital', 'Digital Hosts', iif({base_persona} = 'The Host' and {online_realm} = 'hybrid', 'Hybrid Hosts', iif({base_persona} = 'The Host', 'Hosts', iif({base_persona} = 'The Curator' and {online_realm} = 'digital', 'Digital Curators', iif({base_persona} = 'The Curator', 'Curators', 'Curators'))))))))))))"
    },
    {
      "name": "persona_match",
      "expression": "iif({base_persona} = 'The Solo Strategist', '94', iif({base_persona} = 'The Try-Before-You-Buyer', '87', iif({base_persona} = 'The Social Gamer', '91', iif({base_persona} = 'The Host', '89', '93'))))"
    },
    {
      "name": "benefit_icon_1",
      "expression": "iif({base_persona} = 'The Solo Strategist' and {online_realm} = 'digital', '📊', iif({base_persona} = 'The Solo Strategist', '📊', iif({base_persona} = 'The Try-Before-You-Buyer' and {online_realm} = 'digital', '🎮', iif({base_persona} = 'The Try-Before-You-Buyer', '🎮', iif({base_persona} = 'The Social Gamer' and {online_realm} = 'digital', '🎉', iif({base_persona} = 'The Social Gamer' and {online_realm} = 'hybrid', '🎉', iif({base_persona} = 'The Social Gamer', '🎉', iif({base_persona} = 'The Host' and {online_realm} = 'digital', '🎯', iif({base_persona} = 'The Host' and {online_realm} = 'hybrid', '🎯', iif({base_persona} = 'The Host', '🎯', iif({base_persona} = 'The Curator' and {online_realm} = 'digital', '🔍', iif({base_persona} = 'The Curator', '🔍', '🔍'))))))))))))"
    },
    {
      "name": "benefit_text_1",
      "expression": "iif({base_persona} = 'The Solo Strategist' and {online_realm} = 'digital', 'Play tracking', iif({base_persona} = 'The Solo Strategist', 'Play tracking', iif({base_persona} = 'The Try-Before-You-Buyer' and {online_realm} = 'digital', 'BGA integration', iif({base_persona} = 'The Try-Before-You-Buyer', 'Online play badge', iif({base_persona} = 'The Social Gamer' and {online_realm} = 'digital', 'Party game recs', iif({base_persona} = 'The Social Gamer' and {online_realm} = 'hybrid', 'Party game recs', iif({base_persona} = 'The Social Gamer', 'Party game recs', iif({base_persona} = 'The Host' and {online_realm} = 'digital', 'Mood matcher', iif({base_persona} = 'The Host' and {online_realm} = 'hybrid', 'Mood matcher', iif({base_persona} = 'The Host', 'Mood matcher', iif({base_persona} = 'The Curator' and {online_realm} = 'digital', 'Similarity search', iif({base_persona} = 'The Curator', 'Similarity search', 'Similarity search'))))))))))))"
    },
    {
      "name": "benefit_icon_2",
      "expression": "iif({base_persona} = 'The Solo Strategist' and {online_realm} = 'digital', '🎯', iif({base_persona} = 'The Solo Strategist', '📈', iif({base_persona} = 'The Try-Before-You-Buyer' and {online_realm} = 'digital', '💰', iif({base_persona} = 'The Try-Before-You-Buyer', '💰', iif({base_persona} = 'The Social Gamer' and {online_realm} = 'digital', '👥', iif({base_persona} = 'The Social Gamer' and {online_realm} = 'hybrid', '👥', iif({base_persona} = 'The Social Gamer', '👥', iif({base_persona} = 'The Host' and {online_realm} = 'digital', '📚', iif({base_persona} = 'The Host' and {online_realm} = 'hybrid', '📚', iif({base_persona} = 'The Host', '📚', iif({base_persona} = 'The Curator' and {online_realm} = 'digital', '📊', iif({base_persona} = 'The Curator', '📊', '📊'))))))))))))"
    },
    {
      "name": "benefit_text_2",
      "expression": "iif({base_persona} = 'The Solo Strategist' and {online_realm} = 'digital', 'Solo mode finder', iif({base_persona} = 'The Solo Strategist', 'Stats dashboard', iif({base_persona} = 'The Try-Before-You-Buyer' and {online_realm} = 'digital', 'Price comparison', iif({base_persona} = 'The Try-Before-You-Buyer', 'Price comparison', iif({base_persona} = 'The Social Gamer' and {online_realm} = 'digital', 'Shared collections', iif({base_persona} = 'The Social Gamer' and {online_realm} = 'hybrid', 'Shared collections', iif({base_persona} = 'The Social Gamer', 'Shared collections', iif({base_persona} = 'The Host' and {online_realm} = 'digital', 'Quick rules refresher', iif({base_persona} = 'The Host' and {online_realm} = 'hybrid', 'Quick rules refresher', iif({base_persona} = 'The Host', 'Quick rules refresher', iif({base_persona} = 'The Curator' and {online_realm} = 'digital', 'Collection insights', iif({base_persona} = 'The Curator', 'Collection insights', 'Collection insights'))))))))))))"
    },
    {
      "name": "benefit_icon_3",
      "expression": "iif({base_persona} = 'The Solo Strategist' and {online_realm} = 'digital', '🔄', iif({base_persona} = 'The Solo Strategist', '🎯', iif({base_persona} = 'The Try-Before-You-Buyer' and {online_realm} = 'digital', '✅', iif({base_persona} = 'The Try-Before-You-Buyer', '📊', iif({base_persona} = 'The Social Gamer' and {online_realm} = 'digital', '🌐', iif({base_persona} = 'The Social Gamer' and {online_realm} = 'hybrid', '🔄', iif({base_persona} = 'The Social Gamer', '👤', iif({base_persona} = 'The Host' and {online_realm} = 'digital', '💻', iif({base_persona} = 'The Host' and {online_realm} = 'hybrid', '🔄', iif({base_persona} = 'The Host', '⚡', iif({base_persona} = 'The Curator' and {online_realm} = 'digital', '💻', iif({base_persona} = 'The Curator', '💰', '💰'))))))))))))"
    },
    {
      "name": "benefit_text_3",
      "expression": "iif({base_persona} = 'The Solo Strategist' and {online_realm} = 'digital', 'Cross-platform sync', iif({base_persona} = 'The Solo Strategist', 'Solo mode finder', iif({base_persona} = 'The Try-Before-You-Buyer' and {online_realm} = 'digital', 'Digital try-before-you-buy', iif({base_persona} = 'The Try-Before-You-Buyer', 'BGA integration', iif({base_persona} = 'The Social Gamer' and {online_realm} = 'digital', 'Cross-platform parties', iif({base_persona} = 'The Social Gamer' and {online_realm} = 'hybrid', 'Hybrid game finder', iif({base_persona} = 'The Social Gamer', 'Player count optimizer', iif({base_persona} = 'The Host' and {online_realm} = 'digital', 'Virtual table setup', iif({base_persona} = 'The Host' and {online_realm} = 'hybrid', 'Cross-platform coordination', iif({base_persona} = 'The Host', 'Decision engine', iif({base_persona} = 'The Curator' and {online_realm} = 'digital', 'Digital library sync', iif({base_persona} = 'The Curator', 'Price tracker', 'Price tracker'))))))))))))"
    }
  ],
  "showProgressBar": true,
  "checkErrorsMode": "onValueChanged",
  "startSurveyText": "Start Quiz",
  "completeText": "Reveal My Superpower",
  "widthMode": "responsive"
} as const;
